/**
 * @fileoverview Helper class for image and icon path resolution.
 * Centralizes icon lookup for POI items, advertisement images,
 * and pin image list preparation. Loads icon mappings from JSON data.
 * @module helpers/ImageHelper
 */

import { loadJsonDataFromPath, appendCacheBuster, escapeHtml } from '../utils/jrfuncs.js';
import { calcCategoryTail } from '../utils/nynfuncs.js';


/**
 * Helper class for image and icon path resolution.
 * Provides methods for determining which icon to display for a POI item,
 * resolving advertisement image URLs, and preparing pin image lists.
 * Icon mappings are loaded asynchronously from a JSON data file.
 * @class
 */
export class ImageHelper {
    /**
     * Creates a new ImageHelper instance.
     * @param {Object} mapApp - The MapApp instance
     */
    constructor(mapApp) {
        this.mapApp = mapApp;
        this.overrideImageMap = {};
        this.pcatgIconImageMap = {};
        this.ptypeIcons = {};
        this.imageLists = {};
    }




    // ==========================================
    // HELPERS
    // ==========================================



    /**
     * Loads the icon mapping data from the JSON file.
     * Must be called during initialization before any icon lookups.
     * @async
     * @returns {Promise<void>}
     */
    async loadIconMapFromPath(path) {
        const trailingSlash = path.endsWith('/') ? '' : '/';
        let filePath = this.resolveStaticPath(`${path}${trailingSlash}iconMap.json`);
        // the icon map and image lists are SHARED by every world, so the APP version busts them (any
        // version bump changes it), never a world's own (see MapApp.getAppCacheBusterVersion)
        filePath = appendCacheBuster(filePath, this.mapApp.getAppCacheBusterVersion());
        //
        const data = await loadJsonDataFromPath(filePath, 'pcat icon map');
        await this.loadIconMapFromData(data);
    }


    async loadIconMapFromData(data) {
        this.overrideImageMap = data.overrideMap || {};
        this.pcatgIconImageMap = data.pcatMap || {};
        this.ptypeIcons = data.ptypeMap || {};
    }


    /**
     * Loads an image list JSON file from the given directory path and stores it under the given key.
     * The JSON file is expected to be named "imageList_${key}.json" and contain a flat array
     * of image file paths relative to the "images/" directory.
     * @async
     * @param {string} path - Directory path where the JSON file resides (e.g., "data/imageMaps/")
     * @param {string} key - Key used both to derive the filename and to store the list internally
     * @returns {Promise<void>}
     */
    async loadImageList(path, key) {
        const trailingSlash = path.endsWith('/') ? '' : '/';
        let filePath = this.resolveStaticPath(`${path}${trailingSlash}imageList_${key}.json`);
        filePath = appendCacheBuster(filePath, this.mapApp.getAppCacheBusterVersion());
        //
        const data = await loadJsonDataFromPath(filePath, `image list: ${key}`);
        this.imageLists[key] = data || [];
    }


    /**
     * Loads multiple image lists from the same directory path.
     * @async
     * @param {string} path - Directory path where the JSON files reside
     * @param {string[]} keyList - Array of keys to load
     * @returns {Promise<void>}
     */
    async loadImageLists(path, keyList) {
        for (const key of keyList) {
            await this.loadImageList(path, key);
        }
    }





    // ==========================================
    // ACCESSORS
    // ==========================================


    /**
     * Returns the override image map (pcat/pcatg overrides).
     * @returns {Object<string, string>}
     */
    getOverrideImageMap() {
        return this.overrideImageMap;
    }

    /**
     * Returns the standard pcatg-to-icon image map.
     * @returns {Object<string, string>}
     */
    getPcatgIconImageMap() {
        return this.pcatgIconImageMap;
    }

    /**
     * Returns the ptype-to-icon map.
     * @returns {Object<string, string>}
     */
    getPtypeIcons() {
        return this.ptypeIcons;
    }





    // ==========================================
    // HELPERS
    // ==========================================



    /**
     * Resolves a relative asset path using the configured staticBasePath.
     * Delegates to MapApp.resolveStaticPath.
     * @param {string} relativePath - Relative path to an asset
     * @returns {string} Resolved path
     * @private
     */
    resolveStaticPath(relativePath) {
        return this.mapApp.resolveStaticPath(relativePath);
    }










    // ==========================================
    // IMAGE LISTS
    // ==========================================




    /**
     * Returns the image list stored under the given key.
     * @param {string} key - The key used when the list was loaded via loadImageList
     * @returns {Array<string>} Array of relative image file paths, or empty array if key not found
     */
    getImageListUnderKey(key) {
        return this.imageLists[key] || [];
    }


    /**
     * Finds an image list entry by path across the given keys.
     * @param {Array<string>} keyList - Array of image list keys to search
     * @param {string} path - The path value to match
     * @returns {Object|null} The matching image list entry, or null if not found
     */
    findItemInImageLists(keyList, path) {
        for (const key of keyList) {
            const items = this.getImageListUnderKey(key);
            const found = items.find(item => item.path === path);
            if (found) return found;
        }
        return null;
    }


    /**
     * Returns the resolved image src for a path across the given keys.
     * Falls back to the first non-missing item if the path is not found.
     * @param {Array<string>} keyList - Array of image list keys to search
     * @param {string} path - The path value to match
     * @returns {string} Resolved image src path
     */
    findImageSrcInImageLists(keyList, path) {
        const item = this.findItemInImageLists(keyList, path);
        if (item) return this.resolveStaticPath('images/' + item.path);
        // fallback to first non-missing item
        for (const key of keyList) {
            const items = this.getImageListUnderKey(key);
            const first = items.find(i => !i.missing);
            if (first) return this.resolveStaticPath('images/' + first.path);
        }
        return '';
    }


    /**
     * Returns the caption for a path across the given keys.
     * @param {Array<string>} keyList - Array of image list keys to search
     * @param {string} path - The path value to match
     * @returns {string} Caption string, or empty string if not found
     */
    findCaptionInImageLists(keyList, path) {
        const item = this.findItemInImageLists(keyList, path);
        return item ? (item.caption || item.path) : '';
    }


    /**
     * Renders image list entries from the given keys as icon picker grid button HTML.
     * Produces buttons compatible with the BookmarkDialog icon picker grid,
     * using CSS mask-image for colorable SVG icon display.
     * @param {Array<string>} keyList - Array of image list keys to include
     * @param {string} currentValue - Currently selected pin value (gets 'selected' class)
     * @param {string} currentColor - Background color for the icon buttons
     * @returns {string} HTML string of icon-picker-item buttons
     */
    renderImageListsAsIconGridHtml(keyList, currentValue, currentColor) {
        const buttons = [];
        // currentColor is the bookmark's (user-set or imported) colour, so it is escaped like the rest
        const colorAttr = escapeHtml(currentColor);
        for (const key of keyList) {
            const items = this.getImageListUnderKey(key);
            for (const item of items) {
                if (item.missing) continue;
                const imageSrc = escapeHtml(this.resolveStaticPath('images/' + item.path));
                const caption = escapeHtml(item.caption || item.path);
                const selected = item.path === currentValue ? 'selected' : '';
                buttons.push(
                    `<button type="button" class="icon-picker-item ${selected}"` +
                    ` data-value="${escapeHtml(item.path)}"` +
                    ` data-image="${imageSrc}"` +
                    ` data-caption="${caption}"` +
                    ` title="${caption}"` +
                    ` style="-webkit-mask-image: url('${imageSrc}'); mask-image: url('${imageSrc}'); background-color: ${colorAttr};"` +
                    `></button>`
                );
            }
        }
        return buttons.join('');
    }


    renderImageListsAsMarkerStyleButtonsHtml(keyList) {
        const buttons = [];
        for (const key of keyList) {
            const items = this.getImageListUnderKey(key);
            for (const item of items) {
                if (item.missing) continue;
                // image-list values are data (a json file), escaped like every other attribute/text value
                const imageSrc = escapeHtml(this.resolveStaticPath('images/' + item.path));
                const caption = escapeHtml(item.caption || item.path);
                const color = escapeHtml(item.color || '');
                buttons.push(
                    `<button type="button" class="marker-style-item" data-value="${escapeHtml(item.path)}" title="${caption}">` +
                    `<img class="marker-style-img" src="${imageSrc}" data-color="${color}" alt="${caption}">` +
                    `<span class="marker-style-label">${caption}</span>` +
                    `</button>`
                );
            }
        }
        return buttons.join('');
    }


    // ==========================================
    // ICON LOOKUP
    // ==========================================


    /**
     * Calculates the icon image path for a POI item.
     * Tries the override map, then the standard pcatg map (each over the raw pcat, the raw
     * pcatg, and for composite "A>B|C" values the text after the last '|'), then the ptype
     * mapping, falling back to the default icon. See calcBaseImagePathForItemFromPact.
     * @param {Object} item - The POI item to calculate the icon for
     * @returns {string} Resolved path to the icon image
     */
    calcIconPathForItem(item) {
        const imageRelPath = this.calcBaseImagePathForItemFromPact(item);
        // return it with static path
        return this.resolveStaticPath("images/" + imageRelPath);
    }


    /**
     * Calculates the icon image path for a POI item.
     * Tries pcat override, then pcatg override, then pcatg standard mapping,
     * then ptype mapping, falling back to a default icon.
     * @param {Object} item - The POI item to calculate the icon for
     * @returns {string} Resolved path to the icon image
     */
    calcIconPathForRelativeImagePath(imageRelPath) {
        // return it with static path
        return this.resolveStaticPath("images/" + imageRelPath);
    }


    /**
     * Returns the ordered list of keys to try in the icon maps for a POI item: the raw
     * pcat, the raw pcatg, and then -- for composite values such as
     * "Outer Boroughs>Brooklyn|Airports" -- the text after the LAST '|' of each
     * ("Airports", via nynfuncs.calcCategoryTail, which the yellow pages category search
     * shares). Full values come first so a composite can still be given an entry of its own;
     * the tail is what the nynoir data means as the category.
     * MIRRORED in python by calcIconLookupKeys() in tools/mapDeployTools.py (checkIconMap);
     * change both together.
     * @param {Object} item - The POI item
     * @returns {string[]} Candidate keys, de-duplicated, empties dropped
     */
    calcIconLookupKeysForItem(item) {
        const keys = [];
        const pushKey = (key) => {
            if (key && !keys.includes(key)) {
                keys.push(key);
            }
        };
        pushKey(item.pcat);
        pushKey(item.pcatg);
        for (const rawValue of [item.pcat, item.pcatg]) {
            const tail = calcCategoryTail(rawValue);
            if (tail && tail !== rawValue) {
                pushKey(tail);
            }
        }
        return keys;
    }


    calcBaseImagePathForItemFromPact(item) {
        const keys = this.calcIconLookupKeysForItem(item);

        // override mappings first, then the standard pcatg mapping; within each map the full
        // pcat/pcatg values are tried before their '|' tails (see calcIconLookupKeysForItem)
        let imageRelPath = null;
        for (const imageMap of [this.overrideImageMap, this.pcatgIconImageMap]) {
            for (const key of keys) {
                if (imageMap[key]) {
                    imageRelPath = imageMap[key];
                    break;
                }
            }
            if (imageRelPath) {
                break;
            }
        }

        // ptype fallback
        if (!imageRelPath) {
            // try ptype matching
            const ptype = item.ptype;
            if (this.ptypeIcons[ptype]) {
                imageRelPath = this.ptypeIcons[ptype];
            }
        }

        // finally default (a hit here is a mapping bug: run `python tools/mapDeployTools.py checkIconMap`)
        if (!imageRelPath) {
            console.log(`WARNING: could not locate image with pcat = "${item.pcat}" or pcatg = "${item.pcatg}".`);
            imageRelPath = this.ptypeIcons.default;
        }

        return imageRelPath;
    }


    calcUserImagePathForItem(item) {
        const imageRelPath = this.calcUserImagePathForItemRelative(item);
        // return it with static path
        const retv = this.resolveStaticPath("images/" + imageRelPath);
        return retv;
    }

    calcUserImagePathForItemRelative(item) {
        let imageRelPath = this.calcBaseImagePathForItemFromPact(item);
        if (imageRelPath.startsWith('icons/')) {
            imageRelPath = 'user/' + imageRelPath.substring(6);
        }
        return imageRelPath;
    }
}
